import {
  GoAAccordion,
  GoAButton,
  GoACheckbox,
  GoAFormItem,
  GoAIconButton,
  GoAInput,
  GoARadioGroup,
  GoARadioItem,
  GoATextArea
} from "@abgov/react-components";
import { Order } from "../../model/Order";
import { ORDER_FROM_AREA_LOGISTICS, ORDER_FROM_AREA_WAREHOUSE, ORDER_STATUS_CANCELLED, ORDER_STATUS_COMPLETE, ORDER_STATUS_DRAFT, ORDER_STATUS_ORDERED, orderFromOptions } from "../../common/constants";
import {
  Autocomplete,
  Breadcrumbs,
  debounce,
  Link,
  TextField,
  Typography,
  useMediaQuery
} from "@mui/material";
import { SetStateAction, useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { forestAreaState, orderPdfState, orderState } from "../../states/Order.state";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from 'dayjs/plugin/timezone';
import { SelectOptions } from "../../model/Base";
import { ExternalService, IFilter, IPagination, ISearch } from "../../services/ExternalService";
import { OrderService } from "../../services/OrderService";
import EmailPicker from "../../components/EmailPicker";
import { pdfService } from "../../services/PdfService";
import { OrderPdf } from "../../model/OrderPdf";
import PageLoader from "../../components/PageLoader";
import TruncatedText from "../../components/TruncatedText";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OrderStatusBadge from "../../components/OrderStatusBadge";
import { notificationMessageState, showNotificationState } from "../../states/Common.state";
import { INotificationMessage } from "../../model/Common";
import { CustomFmF } from "../../components/datePicker";
import { getUserEmail, getUserName } from "../../utils/tokens";
import { useAuth } from "react-oidc-context";
import UserNamePicker from "../../components/UserNamePicker";
import EmailPickerSingle from "../../components/EmailPickerSingle";

dayjs.extend(utc);
dayjs.extend(timezone);

interface IOrderDetailSummaryProps {
  orderData: Order;
  isProcessor: boolean;
}

// interface StatusMarginMap {
//   [key: number]: string;
// }


const OrderDetailSummary: React.FC<IOrderDetailSummaryProps> = (props) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1366px)');
  const [editMode, setEditMode] = useState<boolean>(false);
  const [orderDetails, setOrderDetails] = useRecoilState<Order>(orderState);
  const [orderPdf, setOrderPdf] = useRecoilState<OrderPdf>(orderPdfState);
  const [fireNumberOptions, setFireNumberOptions] = useState<SelectOptions[]>(
    []
  );
  const [forestAreaOptions, setForestAreaOptions] = useRecoilState<SelectOptions[]>(forestAreaState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [userSelectedList, setUserSelectedList] = useState<string>('');
  const [selectedWatcherList, setSelectedWatcherList] = useState<string>('');
  const [loading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [watcherError, setWatcherError] = useState("");
  const [showNotification, setShowNotification] = useRecoilState<boolean>(showNotificationState);
  const [notificationMessage, setNotificationMessage] = useRecoilState<INotificationMessage>(notificationMessageState);
  const [emailRequired, setEmailRequired] = useState<boolean>(false);
  const [defaultEmailAddress, setDefaultEmailAddress] = useState<string>('');
  const [orderingForEmail, setOrderingForEmail] = useState<string>(props.orderData.orderingForEmail ?? '');
  const [orderingForEmailValidationError, setOrderingForEmailValidationError] = useState<string>("");
  const [orderingForNameValidationError, setOrderingForNameValidationError] = useState("");
  const [orderOnBehalf, setOrderOnBehalf] = useState<boolean>(props.orderData.isOrderingForOtherPerson);
  const [crewNumberOptions, setCrewNumberOptions] = useState<any[]>([]); // Empty initially
  const [crewTypeOptions, setCrewTypeOptions] = useState<any[]>([]); // Empty initially

  const { orderData } = props;


  let formattedDate = '';

  const date = orderData.orderDate || orderDetails.orderDate;
  if (date) {
    formattedDate = orderData.orderDate
      ? dayjs.utc(orderData.orderDate).tz("America/Edmonton").format("YYYY-MM-DD HH:mm")
      : dayjs(date).format("YYYY-MM-DD HH:mm");
  }


  useEffect(() => {
    if (orderDetails?.emailAddress && !(orderDetails.orderFrom?.toLowerCase() ===
      ORDER_FROM_AREA_LOGISTICS.toLowerCase() ||
      orderDetails.orderFrom?.toLowerCase() ===
      ORDER_FROM_AREA_WAREHOUSE.toLowerCase())) {
      setUserSelectedList(orderDetails.emailAddress);
    } else { setUserSelectedList(''); }

    setEmailRequired(
      orderDetails.orderFrom === "Incident Logistics" || orderDetails.orderFrom === "Other"
    );
    if (orderDetails?.watchers) {
      setSelectedWatcherList(orderDetails.watchers);
    }

  }, [orderDetails]);


  useEffect(() => {
    if (defaultEmailAddress) {
      setOrderDetails((prevDetails) => ({
        ...prevDetails,
        emailAddress: defaultEmailAddress,
      }));
    }
  }, [defaultEmailAddress, setOrderDetails]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNotification(false);
    }, 30000);

    // Cleanup timer on unmount
    return () => clearTimeout(timer);
  }, [showNotification, setShowNotification]);


  const onFireNoSearchChange = (inputValue: string) => {
    const lowerCasedInput = inputValue.toLowerCase();
    loadFireOptions(lowerCasedInput, (result: any) => {
      if (result) {
        const options = result?.map((r: any) => ({
          label: `${r.fireNumber}-${r.year}`, // Display text for the dropdown
          value: `${r.fireNumber}-${r.year}`, // Underlying value
        }));
        setFireNumberOptions(options);
      }
    });
  };

  const loadFireOptions = debounce(async (inputValue: string, callback: (data: any[]) => void) => {
    if (!inputValue) return;
    const currentYearFireNumbers = await ExternalService.getFireNumbers(inputValue) || [];
    // Pass the transformed options to the callback function
    callback(currentYearFireNumbers);
  }, 500);



  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {

    const loadCrewTypes = async (inputValue: string) => {
      const crewTypes = await ExternalService.getCrewTypes(inputValue) || [];
      // Pass the transformed options to the callback function
      return crewTypes;
    }
    loadCrewTypes("").then((result: any) => {
      if (result) {
        const options = result?.map((r: any) => ({
          label: `${r.crewTypeName}`,
          value: r.crewTypeId,
          oracleId: r.oracleId
        }));
        setCrewTypeOptions(options);
      }
    });
  }, []); // Empty dependency array to run only once on mount

  const loadCrewNumbers = debounce(async (inputValue: string, crewType: number, callback: (data: any[]) => void) => {
    if (!inputValue) return;
    const crewNumbers = await ExternalService.getCrewNumbers(inputValue, crewType) || [];
    // Pass the transformed options to the callback function
    callback(crewNumbers);
  }, 500);

  const onCrewNumberSearchChange = (inputValue: string) => {
    const lowerCasedInput = inputValue.trim().toLowerCase();
    const crewType = crewTypeOptions.find(i => i.value === props.orderData.crewTypeId)?.oracleId;
    loadCrewNumbers(lowerCasedInput, crewType, (result: any) => {
      if (result) {
        const options = result?.map((r: any) => ({
          label: `${r.crewNumber}`,
          value: `${r.crewNumber}`,
          crewType: r.crewTypeId // Include crewTypeId for further processing if needed
        }));
        setCrewNumberOptions(options);
      }
    });
  };


  //const isTabletOrMobile = useMediaQuery("(max-width: 1024px)");

  const handleEditOrderSummary = async () => {
    setEditMode(true);
    setShowNotification(false);
  };

  const handleUpdateOrderSummary = async () => {
    if (!validateForm('save')) {
      return;
    }

    setIsLoading(true);
    let retryCount = 0;
    const maxRetries = 1; // You can adjust this if needed

    while (retryCount <= maxRetries) {
      const result = await OrderService.updateOrderSummary(orderDetails);

      if (result.success) {
        setEditMode(false);
        setOrderPdf({ ...orderPdf, data: orderDetails });
        setShowNotification(true);
        setNotificationMessage({ messageType: 'success', messageHeader: 'Order Summary successfully saved.' });
        console.log(showNotification + "-" + notificationMessage);
        setIsLoading(false);
        return; // Exit the loop if successful
      }

      retryCount++;
      console.warn(`Update failed. Retrying... (${retryCount})`);
    }

    setIsLoading(false);
    setShowNotification(true);
    setNotificationMessage({ messageType: 'error', messageHeader: 'Failed to save Order Summary after retry.' });
  };

  const handleCancelUpdateOrderSummaryChanges = async () => {
    try {
      const order = await OrderService.getByOrderNumber(orderData.orderNumber);
      setOrderDetails(order.data);
      setEditMode(false);
    } catch (error) {
      console.error("Error fetching order details:", error);
    }
  };


  const handleOrderDateChange = (value: any) => {
    const inputDate = new Date(value.target.value);

    if (isNaN(inputDate.getTime())) {
      console.error("Invalid date format");
      return;
    }

    const selectedDate = dayjs(inputDate)
      .tz("America/Edmonton", true) // Treat the input date as already in America/Edmonton timezone
      .set('hour', 0)
      .set('minute', 1)
      .set('second', 0)
      .set('millisecond', 0).toDate();


    handleFieldChange("orderDate", selectedDate);
  };

  const handleFieldChange = async (fieldName: string, fieldValue: any) => {
    if (fieldName === "orderDate") {
      setOrderDetails({ ...orderDetails, orderDate: fieldValue, orderDatePrintable: dayjs.utc(fieldValue).tz("America/Edmonton", false).format("YYYY-MM-DD HH:mm") });
    }
    if (fieldName === "crewTypeId") {
      if (fieldValue !== orderDetails.crewTypeId) {
        setOrderDetails({ ...orderDetails, crewTypeId: fieldValue, crewTypeName: crewTypeOptions.find((x) => x.value === fieldValue)?.label || '', crewNumber: '' });
        setCrewNumberOptions([]); // Reset crewNumberOptions when crewTypeId changes
      }
    }
    if (fieldName === "crewTypeName") {
      setOrderDetails({ ...orderDetails, crewTypeName: fieldValue });
    }
    if (fieldName === "crewNumber") {
      if (!orderDetails.crewTypeId || orderDetails.crewTypeId === 0) {
        const selectedCrew = crewNumberOptions.find(i => String(i.value) === String(fieldValue));
        const crewTypeOracleId = Number(selectedCrew?.crewType);

        if (crewTypeOracleId) {
          const crewTypeMatch = crewTypeOptions.find(i => String(i.oracleId) === String(crewTypeOracleId));
          setOrderDetails({
            ...orderDetails,
            crewNumber: fieldValue,
            crewTypeId: Number(crewTypeMatch?.value),
            crewTypeName: crewTypeMatch?.label
          });
        } else {
          setOrderDetails({
            ...orderDetails,
            crewNumber: fieldValue
          });
        }
      } else {
        setOrderDetails({
          ...orderDetails,
          crewNumber: fieldValue
        });
      }
    }
    if (fieldName === "phoneNumber") {
      setOrderDetails({ ...orderDetails, phoneNumber: fieldValue });
    }
    if (fieldName === "orderFrom") {
      if ((fieldValue && (fieldValue.toLowerCase() ===
        ORDER_FROM_AREA_LOGISTICS.toLowerCase() ||
        fieldValue.toLowerCase() ===
        ORDER_FROM_AREA_WAREHOUSE.toLowerCase()))) {
        setDefaultEmailAddress(props.orderData.defaultEmails[fieldValue]);
      } else { setDefaultEmailAddress('') }
      setOrderDetails({ ...orderDetails, orderFrom: fieldValue });
    }
    if (fieldName === "forestArea") {
      setOrderDetails({ ...orderDetails, forestArea: fieldValue });
    }
    if (fieldName === "requiredBy") {
      setOrderDetails({ ...orderDetails, requiredBy: fieldValue, requiredByPrintable: dayjs.utc(fieldValue).tz("America/Edmonton", false).format("YYYY-MM-DD HH:mm") });
    }
    if (fieldName === "deliveryMethod") {
      setOrderDetails({ ...orderDetails, deliveryMethod: fieldValue, shipTo: fieldValue.toLowerCase() === "pickup" ? "" : orderDetails.shipTo });
    }
    if (fieldName === "shipTo") {
      setOrderDetails({ ...orderDetails, shipTo: fieldValue });
    }
    if (fieldName === "fireNumber") {
      //   const forestArekey = fieldValue?.charAt(0);
      //   const forestArea = forestAreaMap[forestArekey] || null;
      //   const relatedforestArea = forestAreaOptions?.find((x) => x.value === forestArea)?.value || '';
      setOrderDetails({ ...orderDetails, fireNumber: fieldValue });
    }
    if (fieldName === "emailAddress") {
      setOrderDetails({ ...orderDetails, emailAddress: fieldValue });
    }
    if (fieldName === "watchers") {
      setOrderDetails({ ...orderDetails, watchers: fieldValue });
    }
    if (fieldName === "orderType") {
      setOrderDetails({ ...orderDetails, orderType: fieldValue, isPersonnelOrderType: fieldValue === "Personnel" });
    }

    if (fieldName === "orderingForEmail") {
      setOrderDetails({ ...orderDetails, orderingForEmail: fieldValue });
    }

    if (fieldName === "orderingForPhone") {
      setOrderDetails({ ...orderDetails, orderingForPhone: fieldValue });
    }

    if (fieldName === "orderOnBehalf") {
      setOrderDetails({ ...orderDetails, isOrderingForOtherPerson: fieldValue });
    }
    if (fieldName === "phoneNumber") {
      setOrderDetails({ ...orderDetails, phoneNumber: fieldValue });
    }
  };

  const validateForm = useCallback(
    (name: string = "", value: any | string[] = "") => {
      const newErrors: Record<string, string> = {};

      // Check for errors in form fields
      if (name === "save" && (props.orderData.orderDate && props.orderData.orderDate <= new Date("2025-01-01"))) {
        newErrors["orderDate"] = "Please enter a valid order date.";
      }

      if (name === "save" && props.orderData.isOrderAssociatedWithFireNumber && !props.orderData.fireNumber) {
        newErrors["fireNumber"] = "Fire number is required";
      }

      if (name === "save" && !props.orderData.forestArea) {
        newErrors["forestArea"] = "Forest area is required";
      }

      if (name === "save" && !props.orderData.orderType) {
        newErrors["orderType"] = "Order type is required";
      }

      if (name === "save" && !props.orderData.orderFrom) {
        newErrors["orderFrom"] = "Submit order to is required";
      }

      if (name === "save" && props.orderData.deliveryMethod === "Ship to" && !props.orderData.shipTo) {
        newErrors["shipTo"] = "Ship to is required";
      }

      if ((name === "save" && !props.orderData.requiredBy) || (name === "requiredBy" && !value)) {
        newErrors["requiredBy"] = "Required By is required";
      } else if (props.orderData.requiredBy) {
        const requiredByDate = name === "requiredBy" ? new Date(value) : new Date(props.orderData.requiredBy);
        const orderDate = props.orderData.orderDate;

        if ((name === "save" && requiredByDate < new Date()) || (name === "requiredBy" && requiredByDate < new Date())) {
          newErrors["requiredBy"] = "Required By cannot be in the past";
        } else if (orderDate && requiredByDate < new Date(orderDate)) {
          newErrors["requiredBy"] = "Required By cannot be before the Order Date";
        }
      }

      if (emailError) {
        newErrors["emailAddress"] = emailError;
      }

      if (emailRequired && !props.orderData.emailAddress) {
        newErrors["emailAddress"] = "Email address is required";
      }

      if (orderOnBehalf && orderingForEmailValidationError) {
        newErrors["orderingForEmail"] = orderingForEmailValidationError;
      }

      if (orderOnBehalf && orderingForNameValidationError) {
        newErrors["orderingForName"] = orderingForNameValidationError;
      }

      if (orderOnBehalf && !props.orderData.orderingForName) {
        newErrors["orderingForName"] = "Name is required";
      }

      if (orderOnBehalf && !props.orderData.orderingForEmail) {
        newErrors["orderingForEmail"] = "Email address is required";
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0; // Return true if there are no errors
    },
    [props.orderData, emailError, emailRequired, setErrors, orderingForEmailValidationError, orderingForNameValidationError, orderOnBehalf]
  );

  useEffect(() => {
    validateForm();
  }, [emailError, validateForm]);

  const transformToOptions = (inputArray: any[], useLookupFields = false) => {
    return inputArray.map((item, index) => ({
      label: useLookupFields ? item.value : item.name,
      value: useLookupFields ? item.id : item.name,
    }));
  };

  const printOrder = async () => {
    setIsLoading(true);
    try {
      const result = await pdfService.pdfFile(orderPdf);
      if (result) {
        window.open(result.toString(), "_blank", "");
      }
    } catch (error) {
      console.error("An error occurred while opening order pdf", error);
      alert("An error occurred while opening order pdf");
    }
    finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setOrderPdf({ ...orderPdf, data: orderDetails });
    const fireNumber = props.orderData.fireNumber?.split("-");
    if (fireNumber?.length > 0) {
      onFireNoSearchChange(fireNumber[0]);
    }
    if (!forestAreaOptions) {
      let objIPagination: IPagination = { perPage: 20, page: 1 };
      let objIFilter: IFilter = { columnName: "CRT_ID", columnValue: "4" };
      let objISearch: ISearch = {
        search: "",
        filterBy: objIFilter,
        pagination: objIPagination,
      };

      ExternalService.getCorporateRegion(objISearch)
        .then((response: any) => {
          setForestAreaOptions(transformToOptions(response.data));
        })
        .catch((e: Error) => {
          console.error("Error fetching regions:", e);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.orderData.fireNumber, forestAreaOptions]);

  function handleUserSelectedListChange(value: SetStateAction<string>): void {
    setUserSelectedList(value);
    handleFieldChange('emailAddress', value);
  }

  function handleSelectedWatcherListChange(value: SetStateAction<string>): void {
    setSelectedWatcherList(value);
    handleFieldChange('watchers', value);
  }

  // const statusMarginMap: StatusMarginMap = {
  //   [ORDER_STATUS_UNABLETOCOMPLETE]: '65px',
  //   [ORDER_STATUS_PARTIALLYCOMPLETE]: '60px',
  //   [ORDER_STATUS_DRAFT]: '25px',
  //   [ORDER_STATUS_PROCESSING]: '41px'
  //   // Add more statuses and margin values as needed
  // }
    // Get the margin value for the current status, or default to '37px'
  // const marginLeft = statusMarginMap[props.orderData.status] || '37px';

  // const containerStyle = {
  //   marginLeft
  // };
  const auth = useAuth();

  function handleOrderingForNameChange(label: string, value?: string): void {
    setOrderDetails({
      ...orderDetails,
      orderingForName: label.replace(/\s*\(.*?\)/g, ""),
      ...(value && { orderingForEmail: value }) // Assign only if value is not null
    });

    setOrderingForEmail(value ?? '');
  }
  const isMyOrder = (props.orderData.auditTrail?.createdBy === getUserEmail(auth?.user?.access_token ?? "") || props.orderData.orderingForName?.includes(getUserName(auth?.user?.access_token ?? "")));


  function handleOrderingForEmailChange(value: SetStateAction<string>): void {
    setOrderingForEmail(value);
    handleFieldChange('orderingForEmail', value);
  }

  const orderOnBehalfChange = () => {
    setOrderOnBehalf(!orderOnBehalf);
    handleFieldChange('orderOnBehalf', !orderOnBehalf);

  };
  return (
    <>
      <PageLoader visible={loading} />
      <div className="left width100">
        <Breadcrumbs
          className="navigation-buttons"
          aria-label="breadcrumb"
          separator={<ArrowForwardIcon fontSize="small" />}
        >
          <Link
            variant="body2"
            onClick={() => (window.location.pathname = "/ordersList")}
            style={{ cursor: "pointer" }}
          >
            Home
          </Link>
          <Typography color="textPrimary">{`Order: ${props.orderData.orderNumber}`}</Typography>
        </Breadcrumbs>
      </div>
      <div className="left width100">
        <h3 style={{ fontWeight: "600" }}>
          Order: {props.orderData.orderNumber}
        </h3>
      </div>

      <div className="left width100">
        <div className="print-order-button-container">
          <GoAIconButton
            icon="print"
            size="large"
            variant="nocolor"
            title="Print Order"
            onClick={printOrder}
          />
        </div>
      </div>
      <div className="width100 order-summary-container">

        <div className="order-summary-table-top">
          <div>
            <b>Status</b>
            <div>
              <OrderStatusBadge
                orderStatus={props.orderData.status}
                rounded={false}
              />
            </div>
          </div>
          <div>
            <div>Forest area</div>
            {!editMode ? (
              <div>{props.orderData.forestArea}</div>
            ) : (
              <div>
                <GoAFormItem error={errors["forestArea"]}>
                  <GoAInput
                    disabled
                    value={props.orderData.forestArea}
                    name="forestArea"
                    onChange={(item, value) =>
                      handleFieldChange("forestArea", value)
                    }
                  />
                </GoAFormItem>
              </div>
            )}
          </div>
          <div>
            <div>Fire number</div>
            {!editMode ? (
              <div>{props.orderData.fireNumber}</div>
            ) : (
              <div>
                <GoAFormItem error={errors["fireNumber"]}>
                  <Autocomplete
                    className="fire-number"
                    options={fireNumberOptions}
                    getOptionLabel={(option) => option.label}
                    value={
                      fireNumberOptions?.find(
                        (x) => x.value === props.orderData.fireNumber
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      handleFieldChange("fireNumber", newValue?.value); // Update the selected fire number on change
                    }}
                    onInputChange={
                      (event: any, newInputValue) =>
                        onFireNoSearchChange(
                          newInputValue?.includes("-")
                            ? newInputValue?.split("-")[0]
                            : newInputValue
                        ) // Pass the input to handleFieldChange
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Enter fire number"
                        onKeyDown={(e) => {
                          if (e.key === ' ') {
                            e.preventDefault(); // Block space character
                          }
                        }}
                      />
                    )}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 40, // Adjust the height here
                      },
                    }}
                  />
                </GoAFormItem>
              </div>
            )}
          </div>
          <div>
            <div>Delivery method</div>
            {!editMode ? (
              <div>{props.orderData.deliveryMethod}</div>
            ) : (
              <div className="delivery-method">
                <GoAFormItem error={errors['deliveryMethod']}>

                  <GoARadioGroup
                    name="deliveryMethod"
                    orientation={isMobile ? "vertical" : "horizontal"}
                    onChange={(_, value) => handleFieldChange("deliveryMethod", value)}
                    value={props.orderData.deliveryMethod}
                  >
                    <GoARadioItem value="Pickup" label="Pickup"></GoARadioItem>
                    <GoARadioItem value="Ship to" label="Ship to"></GoARadioItem>
                  </GoARadioGroup>
                </GoAFormItem>
              </div>)}
          </div>
          <div>
            <div>Order type</div>
            {!editMode ? (
              <div>{props.orderData.orderType}</div>
            ) : (
              <GoAFormItem error={errors["orderType"]}>
                {/* <Autocomplete
                  className="order-type"
                  options={orderTypeOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    orderTypeOptions?.find(
                      (x) => x.value === props.orderData.orderType
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleFieldChange("orderType", newValue?.value); // Update the selected fire number on change
                  }}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" />
                  )}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 40, // Adjust the height here
                    },
                  }}
                /> */}
                <GoAInput
                  disabled
                  value={props.orderData.orderType}
                  name="orderType"
                  onChange={(item, value) =>
                    handleFieldChange("orderType", value)
                  }
                />
              </GoAFormItem>
            )}
          </div>
        </div>
      </div>
      <div className="width100 order-summary-container">
        <GoAAccordion heading="Order summary" headingSize="medium" open>
          {((!props.isProcessor && isMyOrder &&
            (props.orderData.status === ORDER_STATUS_DRAFT ||
              props.orderData.status === ORDER_STATUS_ORDERED)) ||
            (props.isProcessor &&
              props.orderData.status !== ORDER_STATUS_CANCELLED && props.orderData.status !== ORDER_STATUS_COMPLETE)) &&
            !editMode && (
              <div className={"edit-order-button"}>
                <GoAButton type="tertiary" onClick={handleEditOrderSummary}>
                  Edit
                </GoAButton>
              </div>
            )}
          {((!props.isProcessor && isMyOrder &&
            (props.orderData.status === ORDER_STATUS_DRAFT ||
              props.orderData.status === ORDER_STATUS_ORDERED)) ||
            (props.isProcessor &&
              props.orderData.status !== ORDER_STATUS_CANCELLED && props.orderData.status !== ORDER_STATUS_COMPLETE)) &&
            editMode && (
              <div className={"edit-order-button save-cancel"}>
                <GoAButton type="tertiary" onClick={handleUpdateOrderSummary}>
                  Save
                </GoAButton>
                <GoAButton
                  type="tertiary"
                  onClick={handleCancelUpdateOrderSummaryChanges}
                >
                  Cancel
                </GoAButton>
              </div>
            )}
          <div className="order-summary-table">
            <div>
              <div>Order date</div>
              {!editMode ? (

                <div>
                  {formattedDate}
                </div>

              ) : (
                <div className="order-date">
                  <GoAFormItem error={errors["orderDate"]}>
                    <CustomFmF
                      fontWeight={"100 !important"}
                      clearable={false}
                      height={40}
                      icon={props.orderData.orderDate ? "close" : "calendar"}
                      format="yyyy-MM-dd"
                      value={(orderData.orderDate || orderData.submittedDate) ? new Date(orderData.orderDate || orderData.submittedDate) : null}
                      onChange={(value: Date | null) => handleOrderDateChange(value)}
                      onBlur={(val: any) => { }}
                    />
                  </GoAFormItem>
                </div>
              )}
            </div>
            {orderData.status !== ORDER_STATUS_DRAFT && (
              <div>
                <div>Submitted date</div>
                <div>
                  {props.orderData.submittedDate
                    ? dayjs
                      .utc(props.orderData.submittedDate)
                      .tz("America/Edmonton", false)
                      .format("YYYY-MM-DD HH:mm")
                    : ""}
                </div>
              </div>
            )}
            <div>
              <div>Required by</div>
              {!editMode ? (
                <div>
                  {props.orderData.requiredBy
                    ? dayjs
                      .utc(props.orderData.requiredBy)
                      .tz("America/Edmonton", false)
                      .format("YYYY-MM-DD HH:mm")
                    : ""}
                </div>
              ) : (
                <div style={{ height: "10px" }} className="required-by">
                  <GoAFormItem error={errors["requiredBy"]}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        value={dayjs
                          .utc(props.orderData.requiredBy)
                          .tz("America/Edmonton", false)}
                        ampm={false}
                        onChange={(newValue) => {
                          handleFieldChange("requiredBy", newValue); // Handle null values
                        }}
                        sx={{
                          fontWeight: "400 !important",
                          "& .MuiInputBase-root": {
                            height: 40, // Adjust the height here
                            width: isMobile || isTablet ? "100%" : 250,
                          },
                          "& input": { fontWeight: "100 !important" },
                        }}
                      />
                    </LocalizationProvider>
                  </GoAFormItem>
                </div>
              )}
            </div>
            <div>
              <div>Ordered by</div>
              {!editMode ? (
                <div>{props.orderData.orderBy}</div>
              ) : (
                <div>
                  <GoAFormItem error={errors["orderBy"]}>
                    <GoAInput
                      disabled
                      value={props.orderData.orderBy}
                      ariaLabel={props.orderData.orderBy}
                      name="orderedBy"
                      onChange={(item, value) =>
                        handleFieldChange("orderBy", value)
                      }
                    />
                  </GoAFormItem>
                </div>
              )}
            </div>


            <div>
              <div>Crew type
                <em className="svelte-1l2s7v5 optional-field">  (optional)</em>
              </div>
              {!editMode ? (
                <div>{crewTypeOptions?.find((x) => x.value === props.orderData.crewTypeId)?.label || ''}</div>
              ) : (
                <div>
                  <GoAFormItem requirement="optional">
                    <Autocomplete
                      className="crew-type"
                      options={crewTypeOptions}
                      //  getOptionLabel={(option) => option.label}
                      value={
                        crewTypeOptions?.find((x) => x.value === props.orderData.crewTypeId) || null
                      }
                      onChange={(event, newValue) => {
                        handleFieldChange('crewTypeId', Number(newValue?.value));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder="Select crew type" />
                      )}
                      sx={{
                        '& .MuiInputBase-root': {
                          height: 40
                        },
                      }}
                    />
                  </GoAFormItem>
                </div>
              )}
            </div>
            <div>
              <div>Crew number
                <em className="svelte-1l2s7v5 optional-field">  (optional)</em></div>
              {!editMode ? (
                <div>{props.orderData.crewNumber}</div>
              ) : (
                <div>
                  <GoAFormItem requirement="optional">
                    <Autocomplete
                      className="crew-number"
                      freeSolo // Allow free text input
                      options={crewNumberOptions}
                      // getOptionLabel={(option) => option.label}
                      value={
                        crewNumberOptions?.find((x: any) => x.value === props.orderData.crewNumber) ??
                        (props.orderData.crewNumber ? { label: props.orderData.crewNumber, value: props.orderData.crewNumber } : '')
                      }
                      onChange={(event, newValue) => {
                        handleFieldChange('crewNumber', newValue?.value);
                      }}
                      onInputChange={(event, newInputValue) => {
                        if (event) {
                          onCrewNumberSearchChange(newInputValue);
                          handleFieldChange('crewNumber', newInputValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder="Enter crew number" />
                      )}
                      sx={{
                        '& .MuiInputBase-root': {
                          height: 40
                        },
                      }}
                    />
                  </GoAFormItem>
                </div>
              )}
            </div>
            <div>
              <div>Submit order to</div>
              {!editMode ? (
                <div>{props.orderData.orderFrom}</div>
              ) : (
                <div>
                  <GoAFormItem error={errors["orderFrom"]}>
                    <Autocomplete
                      className="order-from"
                      options={orderFromOptions}
                      value={
                        orderFromOptions?.find(
                          (x) => x.value === props.orderData.orderFrom
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        handleFieldChange("orderFrom", newValue?.value); // Update the selected fire number on change
                      }}
                      disableClearable={false} // Ensures the clear button is enabled
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Enter fire number"
                          onKeyDown={(e) => {
                            if (e.key === ' ') {
                              e.preventDefault(); // Block space character
                            }
                          }}
                        />
                      )}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: 40, // Adjust the height here
                        },
                      }}


                    />
                  </GoAFormItem>
                </div>
              )}
            </div>
            <div className="order-summary-email">
              <div>Email</div>
              {!editMode || ((props.orderData.orderFrom?.toLowerCase() ===
                ORDER_FROM_AREA_LOGISTICS.toLowerCase() ||
                props.orderData.orderFrom?.toLowerCase() ===
                ORDER_FROM_AREA_WAREHOUSE.toLowerCase())) ? (
                <>
                  <TruncatedText
                    text={orderDetails.emailAddress}
                    maxLength={30}
                  />

                </>
              ) : (((orderDetails.orderFrom?.toLowerCase() ===
                ORDER_FROM_AREA_LOGISTICS.toLowerCase() ||
                orderDetails.orderFrom?.toLowerCase() ===
                ORDER_FROM_AREA_WAREHOUSE.toLowerCase())) ? <TruncatedText
                text={defaultEmailAddress}
                maxLength={30}
              /> :
                <GoAFormItem error={errors["emailAddress"]}>
                  <EmailPicker
                    className="email-picker"
                    userSelectedList={userSelectedList}
                    setUserSelectedList={handleUserSelectedListChange}
                    setEmailValidationError={setEmailError}
                  />
                </GoAFormItem>
              )}
            </div>
            <div className="order-summary-watchers">
              <div>Watchers</div>
              {!editMode ?
                <TruncatedText
                  text={orderDetails.watchers}
                  maxLength={30}
                /> :
                (<>
                  <GoAFormItem requirement="optional" error={watcherError}>
                    <EmailPicker
                      className="email-picker"
                      userSelectedList={selectedWatcherList}
                      setUserSelectedList={handleSelectedWatcherListChange}
                      setEmailValidationError={setWatcherError}
                    />
                  </GoAFormItem></>)
              }
            </div>
          </div>
          <hr />
          <div className="order-summary-table section2 width100 order-summary-container">
            <div className="ship-to-container">
              <div>Address or pickup note</div>
              {!editMode ? (
                <div>{props.orderData.shipTo}</div>
              ) : (
                <div className="ship-to">
                  <GoAFormItem error={errors["shipTo"]}>
                    <GoATextArea
                      // disabled={
                      //   props.orderData.deliveryMethod.toLowerCase() ===
                      //   "pickup"
                      // }
                      value={props.orderData.shipTo}
                      width="100%"
                      rows={1}
                      name="shipTo"
                      onChange={(item, value) =>
                        handleFieldChange("shipTo", value)
                      }
                    />
                  </GoAFormItem>
                </div>
              )}
            </div>
            <div>
              <div>Phone
                <em className="svelte-1l2s7v5 optional-field"> (optional)</em></div>
              {!editMode ? (
                <div>{props.orderData.phoneNumber}</div>
              ) : (
                <div className="phone-number">
                  <GoAFormItem>
                    <TextField
                      variant="outlined"
                      fullWidth
                      value={props.orderData.phoneNumber}
                      onChange={(e) => handleFieldChange("phoneNumber", e.target.value)}
                      placeholder="Enter your phone number"
                      inputProps={{
                        maxLength: 25,
                        sx: {
                          height: 12,
                        },
                      }}
                    />
                  </GoAFormItem>
                </div>
              )}
            </div>
          </div>

          {/* <div style={{ display: "none" }}>
                  <div>Watchers</div>
                  {!editMode ? (
                    <div>{props.orderData.watchers}</div>
                  ) : (
                    <div>
                      <GoAInput
                        value={props.orderData.watchers}
                        name="watchers"
                        onChange={(item, value) =>
                          handleFieldChange("watchers", value)
                        }
                      />
                    </div>
                  )}
                </div> */}




          <hr />



          <div className="order-summary-table order-summary-table-order-behalf">
            <div>
              <div>Order on behalf</div>
              <GoACheckbox
                disabled={!editMode}
                name="showOrderonBehalf"
                onChange={
                  orderOnBehalfChange
                }
                checked={orderOnBehalf}
              />
            </div>
            {orderOnBehalf && <div>
              <div>On behalf of</div>
              {!editMode ? (
                <div>{props.orderData.orderingForName?.replace(/\s*\(.*?\)/, '')}</div>
              ) : (
                <div>
                  <GoAFormItem error={errors["orderingForName"]}>
                    <UserNamePicker
                      className="user-name-picker"
                      selectedUserName={props.orderData.orderingForName ?? ''}
                      handleOrderingForNameChange={handleOrderingForNameChange}
                      setValidationError={setOrderingForNameValidationError}
                    />
                  </GoAFormItem>
                </div>
              )}
            </div>
            }

            {orderOnBehalf && <div>
              <div>
                <div>On behalf of email</div>
                {!editMode ? (
                  <div style={{ fontWeight: 'normal' }}>{props.orderData.orderingForEmail}</div>
                ) : (
                  <div>
                    <GoAFormItem error={errors["orderingForEmail"]}>
                      <EmailPickerSingle
                        className="on-behalf-of-email-picker"
                        selectedEmail={orderingForEmail || props.orderData.orderingForEmail || ''}
                        setSelectedEmail={handleOrderingForEmailChange}
                        setValidationError={setOrderingForEmailValidationError}
                      />
                    </GoAFormItem>
                  </div>
                )}
              </div>
            </div>}

            {/* length to 25 */}
            {orderOnBehalf && <div>

              <div>
                <div>On behalf of phone
                  <em className="svelte-1l2s7v5 optional-field"> (optional)</em></div>
                {!editMode ? (
                  <div style={{ fontWeight: 'normal' }}>{props.orderData.orderingForPhone}</div>
                ) : (
                  <div>
                    <GoAFormItem error={errors["orderingForPhone"]} requirement="optional">
                      <GoAInput
                        maxLength={25}
                        value={props.orderData.orderingForPhone}
                        name="orderingForPhone"
                        onChange={(item, value) =>
                          handleFieldChange("orderingForPhone", value)
                        }
                      />
                    </GoAFormItem>
                  </div>
                )}
              </div>
            </div>}
          </div>
        </GoAAccordion>
      </div>
    </>
  );
};
export default OrderDetailSummary;
